import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppLayout } from './components/layout/index.jsx';
import { LoginPage, RegisterPage } from './components/auth/AuthPages';
import { DashboardPage } from './components/dashboard/DashboardPage';
import { Spinner } from './components/common/index.jsx';

// Route-level code-splitting for secondary screens
const TasksPage = lazy(() => import('./components/tasks/TasksPage').then(m => ({ default: m.TasksPage })));
const ArchivePage = lazy(() => import('./components/tasks/ArchivePage').then(m => ({ default: m.ArchivePage })));
const DeadlineRequestsPage = lazy(() => import('./components/tasks/DeadlineRequestsPage').then(m => ({ default: m.DeadlineRequestsPage })));
const AssignmentHistoryPage = lazy(() => import('./components/tasks/AssignmentHistoryPage').then(m => ({ default: m.AssignmentHistoryPage })));
const DepartmentsPage = lazy(() => import('./components/departments/DepartmentsPage').then(m => ({ default: m.DepartmentsPage })));
const DepartmentDetailPage = lazy(() => import('./components/departments/DepartmentDetailPage').then(m => ({ default: m.DepartmentDetailPage })));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const PrivacyPolicyPage = lazy(() => import('./components/public/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsConditionsPage = lazy(() => import('./components/public/TermsConditionsPage').then(m => ({ default: m.TermsConditionsPage })));
const RefundPolicyPage = lazy(() => import('./components/public/RefundPolicyPage').then(m => ({ default: m.RefundPolicyPage })));
const ContactUsPage = lazy(() => import('./components/public/ContactUsPage').then(m => ({ default: m.ContactUsPage })));
const LandingPageWrapper = lazy(() => import('./components/public/LandingPageWrapper').then(m => ({ default: m.LandingPageWrapper })));
const SuperAdminPage = lazy(() => import('./components/superadmin/SuperAdminPage').then(m => ({ default: m.SuperAdminPage })));
const GroupsPage = lazy(() => import('./components/groups/GroupsPage').then(m => ({ default: m.GroupsPage })));
const GroupDetailPage = lazy(() => import('./components/groups/GroupDetailPage').then(m => ({ default: m.GroupDetailPage })));
const MISAdminPage = lazy(() => import('./components/mis/MISAdminPage').then(m => ({ default: m.MISAdminPage })));
const MISUserPage = lazy(() => import('./components/mis/MISUserPage').then(m => ({ default: m.MISUserPage })));
const NotificationsPage = lazy(() => import('./components/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const BroadcastPage = lazy(() => import('./components/notifications/NotificationsPage').then(m => ({ default: m.BroadcastPage })));
const PresencePage = lazy(() => import('./components/presence/PresencePage').then(m => ({ default: m.PresencePage })));
const ReopenRequestsPage = lazy(() => import('./components/reopen/ReopenRequestsPage').then(m => ({ default: m.ReopenRequestsPage })));
const TimesheetPage = lazy(() => import('./components/timesheet/TimesheetPage').then(m => ({ default: m.TimesheetPage })));
const AdminTimesheetPage = lazy(() => import('./components/timesheet/AdminTimesheetPage').then(m => ({ default: m.AdminTimesheetPage })));
const TeamPerformancePage  = lazy(() => import('./components/performance/TeamPerformancePage'));
const AttendancePage      = lazy(() => import('./components/attendance/AttendancePage'));
const AdminAttendancePage  = lazy(() => import('./components/attendance/AdminAttendancePage'));
const PlansPage = lazy(() => import('./components/plans/PlansPage'));
const UsersPage = lazy(() => import('./components/users/UsersPage').then(m => ({ default: m.UsersPage })));
const TeamCalendarPage = lazy(() => import('./components/calendar').then(m => ({ default: m.TeamCalendarPage })));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("React Error Boundary:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center text-2xl font-bold mb-4">
            !
          </div>
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white mb-2">Something went wrong</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-6">
            {this.state.error?.message || "An unexpected error occurred while loading this page."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition-all"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const LoadingScreen = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 text-center">
    <div className="w-16 h-16 rounded-2xl bg-black border border-slate-800 shadow-xl shadow-orange-500/20 p-2.5 flex items-center justify-center mb-4 animate-pulse">
      <img src="/logo.png" alt="KarmShala Logo" className="w-full h-full object-contain" />
    </div>
    <div className="text-brand-600 dark:text-brand-500 mb-2">
      <Spinner size="lg" />
    </div>
    <p className="text-xs font-extrabold text-slate-600 dark:text-slate-300 tracking-wide font-display">
      Loading KarmShala...
    </p>
  </div>
);

const ProtectedRoute = ({ children, adminOnly, managerAllowed, requiredFeature }) => {
  const { user, loading, isAdmin, isSuperAdmin, isManagerOrAdmin, hasFeature } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace/>;
  if (isSuperAdmin && !window.location.pathname.startsWith('/superadmin')) return <Navigate to="/superadmin" replace/>;
  if (adminOnly) {
    if (managerAllowed && !isManagerOrAdmin) return <Navigate to="/dashboard" replace/>;
    if (!managerAllowed && !isAdmin) return <Navigate to="/dashboard" replace/>;
  }
  if (requiredFeature && hasFeature) {
    const isAllowed = typeof requiredFeature === 'function' ? requiredFeature(hasFeature) : hasFeature(requiredFeature);
    if (!isAllowed) return <Navigate to="/dashboard" replace/>;
  }
  return <AppLayout>{children}</AppLayout>;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading, isSuperAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace/>;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace/>;
  return <AppLayout>{children}</AppLayout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isSuperAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={isSuperAdmin ? '/superadmin' : '/dashboard'} replace/>;
  return children;
};




const PrivacyPolicyRoute = () => {
  const { user } = useAuth();
  if (user) return <AppLayout><PrivacyPolicyPage isInsideLayout={true} /></AppLayout>;
  return <PrivacyPolicyPage />;
};

const TermsConditionsRoute = () => {
  const { user } = useAuth();
  if (user) return <AppLayout><TermsConditionsPage isInsideLayout={true} /></AppLayout>;
  return <TermsConditionsPage />;
};

const RefundPolicyRoute = () => {
  const { user } = useAuth();
  if (user) return <AppLayout><RefundPolicyPage isInsideLayout={true} /></AppLayout>;
  return <RefundPolicyPage />;
};

const ContactUsRoute = () => {
  const { user } = useAuth();
  if (user) return <AppLayout><ContactUsPage isInsideLayout={true} /></AppLayout>;
  return <ContactUsPage />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login"          element={<PublicRoute><LoginPage/></PublicRoute>}/>
        <Route path="/signin"         element={<PublicRoute><LoginPage/></PublicRoute>}/>
        <Route path="/signin.html"    element={<PublicRoute><LoginPage/></PublicRoute>}/>
        <Route path="/register"       element={<PublicRoute><RegisterPage/></PublicRoute>}/>
        <Route path="/signup"         element={<PublicRoute><RegisterPage/></PublicRoute>}/>
        <Route path="/signup.html"    element={<PublicRoute><RegisterPage/></PublicRoute>}/>
        <Route path="/privacy-policy" element={<PrivacyPolicyRoute/>}/>
        <Route path="/terms"          element={<TermsConditionsRoute/>}/>
        <Route path="/refund-policy"  element={<RefundPolicyRoute/>}/>
        <Route path="/contact-us"     element={<ContactUsRoute/>}/>
        <Route path="/superadmin"     element={<SuperAdminRoute><SuperAdminPage/></SuperAdminRoute>}/>

        {/* General */}
        <Route path="/dashboard"       element={<ProtectedRoute><DashboardPage/></ProtectedRoute>}/>
        <Route path="/tasks"           element={<ProtectedRoute><TasksPage/></ProtectedRoute>}/>
        <Route path="/departments"     element={<ProtectedRoute><DepartmentsPage/></ProtectedRoute>}/>
        <Route path="/departments/:id" element={<ProtectedRoute><DepartmentDetailPage/></ProtectedRoute>}/>
        <Route path="/users"           element={<ProtectedRoute><UsersPage/></ProtectedRoute>}/>
        <Route path="/groups"          element={<ProtectedRoute><GroupsPage/></ProtectedRoute>}/>
        <Route path="/groups/:id"      element={<ProtectedRoute><GroupDetailPage/></ProtectedRoute>}/>
        <Route path="/notifications"   element={<ProtectedRoute><NotificationsPage/></ProtectedRoute>}/>
        <Route path="/presence"        element={<ProtectedRoute><PresencePage/></ProtectedRoute>}/>
        <Route path="/my-report"       element={<ProtectedRoute><MISUserPage/></ProtectedRoute>}/>
        <Route path="/timesheet"       element={<ProtectedRoute><TimesheetPage/></ProtectedRoute>}/>
        <Route path="/attendance"      element={<ProtectedRoute requiredFeature="attendanceTracking"><AttendancePage /></ProtectedRoute>} />

        {/* Admin only */}
        <Route path="/calendar"          element={<ProtectedRoute adminOnly><TeamCalendarPage/></ProtectedRoute>}/>
        <Route path="/settings"          element={<ProtectedRoute><SettingsPage/></ProtectedRoute>}/>
        <Route path="/mis"               element={<ProtectedRoute adminOnly requiredFeature="misReports"><MISAdminPage/></ProtectedRoute>}/>
        <Route path="/broadcasts"        element={<ProtectedRoute adminOnly><BroadcastPage/></ProtectedRoute>}/>
        <Route path="/reopen-requests"   element={<ProtectedRoute adminOnly managerAllowed><ReopenRequestsPage/></ProtectedRoute>}/>
        <Route path="/archive"           element={<ProtectedRoute><ArchivePage/></ProtectedRoute>}/>
        <Route path="/deadline-requests" element={<ProtectedRoute adminOnly><DeadlineRequestsPage/></ProtectedRoute>}/>
        <Route path="/admin-timesheet"   element={<ProtectedRoute adminOnly><AdminTimesheetPage/></ProtectedRoute>}/>
        <Route path="/team-performance"  element={<ProtectedRoute adminOnly requiredFeature="misReports"><TeamPerformancePage /></ProtectedRoute>} />
        <Route path="/assignment-history" element={<ProtectedRoute adminOnly><AssignmentHistoryPage /></ProtectedRoute>} />
        <Route path="/admin-attendance"  element={<ProtectedRoute adminOnly managerAllowed requiredFeature="attendanceTracking"><AdminAttendancePage /></ProtectedRoute>} />
        <Route path="/plans"             element={<ProtectedRoute adminOnly><PlansPage /></ProtectedRoute>} />

        <Route path="/" element={<LandingPageWrapper />}/>
        <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes/>
          <Toaster position="top-right" toastOptions={{
            duration: 3500,
            style: { fontFamily:'DM Sans, sans-serif', fontSize:'14px', borderRadius:'12px', boxShadow:'0 4px 24px rgba(0,0,0,0.08)', border:'1px solid #f1f5f9' },
            success: { iconTheme: { primary:'#10b981', secondary:'#fff' } },
            error:   { iconTheme: { primary:'#ef4444', secondary:'#fff' } },
          }}/>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
export default App;